// src/pages/AdvisoryDashboard.jsx
//
// Advisor attendance dashboard — King's Academy Faculty Tool Kit
//
// Every advisor sees ONLY their own advisees. The scoping happens server-side
// in getAdvisoryAttendance (email -> /staff -> students.advisorId), so this
// page sends no advisor identity of its own; it just renders what comes back.
// A non-advisor gets a friendly empty state. The advisor (self) view is NOT
// grade-scoped, so Middle School advisors see their grade 7/8 advisees here.
//
// Superadmins/deans get a searchable advisor picker (backed by the listAdvisors
// callable) to inspect any advisor by name, plus an "All Students" view they can
// point at Upper School (default) or Middle School via a division toggle.
//
// COLUMNS ARE DATA-DRIVEN. The function returns `buckets` (ordered
// [{key,label,color}]) defined in /config/attendanceCategories, and this page
// renders one column per bucket. To add/rename/reorder columns, edit that
// Firestore doc — no code change here. Column headers wrap to two lines and the
// grid is sized tight so many buckets still fit without horizontal scroll.
//
// The table's column-header row is frozen (sticky) just below Layout's 56px top
// bar, so it stays visible while scrolling a long roster.
//
// Calls:
//   getAdvisoryAttendance({ startDate, endDate, viewAsStaffId?, allStudents?, division? })
//   listAdvisors()   — superadmin only, populates the picker

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { buildAdvisoryReport } from '../utils/advisoryReportPdf';

/* ------------------------------------------------------------------ */
/* Brand tokens (match MopsDashboard)                                  */
/* ------------------------------------------------------------------ */

const KA = {
  red: '#E83530',
  navy: '#233C5F',
  gold: '#A29164',
  ink: '#1a1a1a',
  muted: '#6b7280',
  line: '#e5e7eb',
  wash: '#f8f8f7',
  white: '#ffffff',
};

const BODY = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const DISPLAY = 'Georgia, serif';

// Height of Layout's sticky top bar — the frozen header row pins just beneath it.
const TOPBAR_H = 56;

/* ------------------------------------------------------------------ */
/* Grade bands — ≥90 green, 80s blue, 70s orange, <70 red, null gray   */
/* ------------------------------------------------------------------ */

const GRADE_BANDS = {
  green: '#2E7D32',
  blue: '#233C5F',
  orange: '#E8830C',
  red: '#E83530',
  gray: '#9ca3af',
};

// Returns the band key for a numeric score, or 'gray' for null/non-numeric.
function bandFor(score) {
  if (score == null || Number.isNaN(Number(score))) return 'gray';
  const n = Number(score);
  if (n >= 90) return 'green';
  if (n >= 80) return 'blue';
  if (n >= 70) return 'orange';
  return 'red';
}

function bandColor(score) {
  return GRADE_BANDS[bandFor(score)];
}

// Traditional half-up rounding to a whole number; passes through null/blank/non-numeric.
const roundScore = (v) =>
  v == null || v === '' || isNaN(Number(v)) ? v : Math.round(Number(v));

// Split a merged course list into graded (non-null score) and ungraded (null).
// Graded sorted lowest-first so problem courses surface.
function splitCourses(courses) {
  const list = Array.isArray(courses) ? courses : [];
  const graded = list
    .filter((c) => c && c.currentScore != null && !Number.isNaN(Number(c.currentScore)))
    .sort((a, b) => Number(a.currentScore) - Number(b.currentScore));
  const ungraded = list.filter((c) => !c || c.currentScore == null || Number.isNaN(Number(c.currentScore)));
  return { graded, ungraded };
}

// Count of graded courses below 70 (the red band) — used to sort/flag at-risk.
function redCount(courses) {
  return splitCourses(courses).graded.filter((c) => Number(c.currentScore) < 70).length;
}

/* ------------------------------------------------------------------ */
/* Date helpers (Amman)                                                */
/* ------------------------------------------------------------------ */

function amman(daysAgo = 0) {
  const d = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Amman',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function schoolYearStart() {
  const today = amman(0);
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const startYear = month >= 8 ? year : year - 1;
  return `${startYear}-08-01`;
}

// The absence "reset" boundary lives in /config/absenceSeasons (same doc the
// Absence Dashboard reads). Default this dashboard's start date to the active
// season's start so a reset — a new season opening — clears these counts too,
// with no code change. Falls back to the school-year start if the config is
// missing or unreachable.
async function activeSeasonStart(dbInst) {
  try {
    const snap = await getDoc(doc(dbInst, 'config', 'absenceSeasons'));
    if (!snap.exists()) return null;
    const seasons = Array.isArray(snap.data().seasons) ? snap.data().seasons : [];
    const today = amman(0);
    const active = seasons.find((s) => {
      if (!s || !s.startDate) return false;
      if (s.startDate > today) return false;      // hasn't started yet
      if (s.endDate && s.endDate < today) return false; // already ended
      return true;                                 // today is inside; open end ok
    });
    return active ? active.startDate : null;
  } catch {
    return null;
  }
}

// Full-ish date with weekday, for the range caption.
function prettyDate(key) {
  if (!key) return '—';
  return new Date(key + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Compact date for table cells: "Aug 28" — no weekday, saves a lot of width.
function shortDate(key) {
  if (!key) return '—';
  return new Date(key + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */

// Compact stat: smaller number + min-width so a full row of them doesn't wrap.
function Stat({ label, value, tone }) {
  return (
    <div style={{ padding: '8px 14px 8px 0', minWidth: 78 }}>
      <div style={{ fontSize: 21, fontFamily: DISPLAY, color: tone || KA.navy, lineHeight: 1.1 }}>
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: KA.muted,
          marginTop: 3,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Btn({ children, onClick, disabled, variant = 'primary', size = 'md', title }) {
  const base = {
    fontFamily: BODY,
    fontWeight: 600,
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    padding: size === 'sm' ? '5px 10px' : '8px 16px',
    fontSize: size === 'sm' ? 12 : 13,
    transition: 'opacity .15s',
  };
  const variants = {
    primary: { background: KA.red, color: KA.white, border: 'none' },
    outline: { background: KA.white, color: KA.navy, border: `1px solid ${KA.navy}` },
    ghost: { background: 'transparent', color: KA.muted, border: `1px solid ${KA.line}` },
  };
  return (
    <button title={title} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>
      {children}
    </button>
  );
}

const inputStyle = {
  padding: '7px 9px',
  border: `1px solid ${KA.line}`,
  borderRadius: 4,
  fontFamily: BODY,
  fontSize: 13,
  boxSizing: 'border-box',
};

// Header cell. `wrap` lets multi-word bucket labels stack to two lines so the
// column can stay narrow; `width` pins a compact width for the numeric columns.
// `sticky` freezes the cell to the top of the viewport (just below Layout's top
// bar) so the whole header row stays visible while the roster scrolls.
function Th({ children, onClick, active, dir, align, width, wrap, sticky }) {
  return (
    <th
      onClick={onClick}
      style={{
        padding: wrap ? '8px 6px' : '9px 10px',
        fontSize: wrap ? 10 : 11,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        lineHeight: 1.15,
        color: active ? KA.navy : KA.muted,
        fontWeight: 600,
        cursor: onClick ? 'pointer' : 'default',
        textAlign: align || 'left',
        whiteSpace: wrap ? 'normal' : 'nowrap',
        verticalAlign: 'bottom',
        width: width || undefined,
        userSelect: 'none',
        ...(sticky
          ? {
              position: 'sticky',
              top: TOPBAR_H,
              zIndex: 3,
              background: KA.wash,
              borderBottom: `1px solid ${KA.line}`,
            }
          : {}),
      }}
    >
      {children}
      {active && <span style={{ marginLeft: 3 }}>{dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function Td({ children, style, align }) {
  return <td style={{ padding: '8px 10px', textAlign: align || 'left', ...style }}>{children}</td>;
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

export default function AdvisoryDashboard() {
  const auth = useAuth() || {};
  const isSuperAdmin = auth.isSuperAdmin === true;
  const isDean = auth.isDean === true;
  const elevated = isSuperAdmin || isDean; // may view any advisor + all-students

  const [startDate, setStartDate] = useState(schoolYearStart());
  const [endDate, setEndDate] = useState(amman(0));
  const [viewAsStaffId, setViewAsStaffId] = useState('');
  const [allStudents, setAllStudents] = useState(false);
  const [division, setDivision] = useState('upper'); // 'upper' | 'middle' — all-students division

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null); // personId

  // Filters (most useful when viewing many students — elevated/all-students).
  const [gradeFilter, setGradeFilter] = useState('');   // '' = all
  const [cohortFilter, setCohortFilter] = useState('');  // '' = all
  const [advisorFilter, setAdvisorFilter] = useState(''); // '' = all
  const [needsAttention, setNeedsAttention] = useState(false); // unexcused abs OR sub-70 course

  // PDF report builder modal
  const [reportMode, setReportMode] = useState(null); // 'single' | 'batch' | null
  const [reportStudent, setReportStudent] = useState(null);

  // Co-advisor management modal (elevated only)
  const [showCoAdvisors, setShowCoAdvisors] = useState(false);

  // Superadmin advisor picker
  const [advisorList, setAdvisorList] = useState([]);
  const [advisorListLoading, setAdvisorListLoading] = useState(false);
  const [advisorQuery, setAdvisorQuery] = useState('');

  const fns = useMemo(() => getFunctions(undefined, 'us-central1'), []);
  const db = useMemo(() => getFirestore(), []);
  const [bootstrapped, setBootstrapped] = useState(false);

  const load = useCallback(
    async (opts = {}) => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable(fns, 'getAdvisoryAttendance');
        const payload = { startDate, endDate };
        // Elevated callers (dean/superadmin) may request all-students (with a
        // division) or a specific advisor. all-students wins if set. Everyone
        // else is pinned to their own advisees server-side regardless.
        const wantAll = opts.allStudents ?? allStudents;
        const asId = opts.viewAsStaffId ?? viewAsStaffId;
        const div = (opts.division ?? division) === 'middle' ? 'middle' : 'upper';
        if (elevated && wantAll) {
          payload.allStudents = true;
          payload.division = div;
        } else if (elevated && asId && String(asId).trim()) {
          payload.viewAsStaffId = String(asId).trim();
        }
        const res = await fn(payload);
        setData(res.data || null);
        setSelected(null);
      } catch (err) {
        setError(err.message || 'Could not load advisory dashboard.');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [fns, startDate, endDate, viewAsStaffId, allStudents, division, elevated]
  );

  // Resolve the default start date from the active absence season once on mount,
  // then load. Gating the first load on this avoids a flash of pre-reset data.
  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await activeSeasonStart(db);
      if (alive && s) setStartDate(s);
      if (alive) setBootstrapped(true);
    })();
    return () => { alive = false; };
  }, [db]);

  // Load once bootstrapped, and on any later date-range change.
  useEffect(() => {
    if (!bootstrapped) return;
    load();
  }, [startDate, endDate, bootstrapped]);

  // Load the advisor picker list once, elevated (dean/superadmin) only.
  // Non-fatal on failure.
  useEffect(() => {
    if (!elevated) return;
    let alive = true;
    (async () => {
      setAdvisorListLoading(true);
      try {
        const fn = httpsCallable(fns, 'listAdvisors');
        const res = await fn({});
        if (alive) setAdvisorList((res.data && res.data.advisors) || []);
      } catch (err) {
        console.error('Could not load advisor list:', err);
      } finally {
        if (alive) setAdvisorListLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [elevated, fns]);

  const advisorMatches = useMemo(() => {
    const t = advisorQuery.trim().toLowerCase();
    if (!t) return [];
    return advisorList.filter((a) => `${a.name} ${a.staffId}`.toLowerCase().includes(t)).slice(0, 8);
  }, [advisorQuery, advisorList]);

  function pickAdvisor(a) {
    setAllStudents(false);
    setViewAsStaffId(a.staffId);
    setAdvisorQuery('');
    load({ viewAsStaffId: a.staffId, allStudents: false });
  }

  // Show the whole school for one division. Default Upper; pass 'middle' for MS.
  function showAllStudents(div = 'upper') {
    const d = div === 'middle' ? 'middle' : 'upper';
    setAllStudents(true);
    setDivision(d);
    setViewAsStaffId('');
    setAdvisorQuery('');
    load({ allStudents: true, division: d });
  }

  function backToMe() {
    setAllStudents(false);
    setDivision('upper');
    setViewAsStaffId('');
    setAdvisorQuery('');
    load({ viewAsStaffId: '', allStudents: false });
  }

  // Column definitions come from the function (config-driven).
  const buckets = useMemo(() => (data && Array.isArray(data.buckets) ? data.buckets : []), [data]);
  const advisees = useMemo(() => (data && Array.isArray(data.advisees) ? data.advisees : []), [data]);

  // Which division the loaded all-students set represents (echoed by the fn).
  const loadedDivision = (data && data.division) === 'middle' ? 'middle' : 'upper';
  const divisionLabel = loadedDivision === 'middle' ? 'Middle School' : 'Upper School';

  // Default the sort to the first bucket once we know what the buckets are.
  useEffect(() => {
    if (!sortKey && buckets.length) setSortKey(buckets[0].key);
  }, [buckets, sortKey]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Bucket keys that count as "unexcused" for the needs-attention filter.
    const unexcusedKeys = buckets.filter((b) => /unexcused/i.test(b.key) || /unexcused/i.test(b.label)).map((b) => b.key);
    let out = advisees.filter((a) => {
      if (term && !`${a.name || ''} ${a.personId || ''}`.toLowerCase().includes(term)) return false;
      if (gradeFilter && String(a.grade || '') !== gradeFilter) return false;
      if (cohortFilter && String(a.cohort || '') !== cohortFilter) return false;
      if (advisorFilter && String(a.advisorName || '') !== advisorFilter) return false;
      if (needsAttention) {
        const hasUnexcused = unexcusedKeys.some((k) => Number(a[k] || 0) > 0);
        const hasFailing = redCount(a.courses) > 0;
        if (!hasUnexcused && !hasFailing) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      if (sortKey === 'name') {
        return String(a.name || '').localeCompare(String(b.name || '')) * dir;
      }
      if (sortKey === 'lastDate') {
        const av = a.lastDate || '';
        const bv = b.lastDate || '';
        if (av === bv) return String(a.name || '').localeCompare(String(b.name || ''));
        return av < bv ? -dir : dir;
      }
      if (sortKey === 'redCount') {
        const av = redCount(a.courses);
        const bv = redCount(b.courses);
        if (av === bv) return String(a.name || '').localeCompare(String(b.name || ''));
        return av < bv ? -dir : dir;
      }
      const av = Number(a[sortKey] || 0);
      const bv = Number(b[sortKey] || 0);
      if (av === bv) return String(a.name || '').localeCompare(String(b.name || ''));
      return av < bv ? -dir : dir;
    });
    return out;
  }, [advisees, buckets, search, sortKey, sortDir, gradeFilter, cohortFilter, advisorFilter, needsAttention]);

  // Distinct filter options from the loaded set.
  const gradeOptions = useMemo(
    () => Array.from(new Set(advisees.map((a) => String(a.grade || '')).filter(Boolean))).sort(),
    [advisees]
  );
  const cohortOptions = useMemo(
    () => Array.from(new Set(advisees.map((a) => String(a.cohort || '')).filter(Boolean))).sort(),
    [advisees]
  );
  const advisorOptions = useMemo(
    () => Array.from(new Set(advisees.map((a) => String(a.advisorName || '')).filter(Boolean))).sort(),
    [advisees]
  );

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  const totals = useMemo(() => {
    const t = {};
    buckets.forEach((b) => (t[b.key] = 0));
    advisees.forEach((a) => {
      buckets.forEach((b) => {
        t[b.key] += Number(a[b.key] || 0);
      });
    });
    return t;
  }, [advisees, buckets]);

  function exportCsv() {
    const head = ['Student', 'ID', 'Grade', 'Cohort', ...buckets.map((b) => b.label), 'Most recent'];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const body = rows.map((r) =>
      [r.name, r.personId, r.grade, r.cohort, ...buckets.map((b) => r[b.key] || 0), r.lastDate || '']
        .map(esc)
        .join(',')
    );
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advisory-attendance-${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const viewingOther = !!(data && data.viewingOther);

  // Tight floor width: fixed columns + a narrow slot per bucket. Lets the table
  // fill the container on desktop rather than forcing a wide scroll.
  const tableMinWidth = 480 + buckets.length * 72;

  /* ---------------- render ---------------- */

  return (
    <div style={{ fontFamily: BODY, color: KA.ink, padding: 24, maxWidth: 1320, margin: '0 auto' }}>
      {/* ---------- header ---------- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          borderBottom: `3px solid ${KA.navy}`,
          paddingBottom: 14,
        }}
      >
        <div>
          <h1 style={{ fontFamily: DISPLAY, color: KA.navy, margin: 0, fontSize: 30, fontWeight: 400 }}>
            Advisory Dashboard
          </h1>
          <div style={{ color: KA.muted, fontSize: 13, marginTop: 4 }}>
            {data && data.allStudents
              ? `All ${divisionLabel} · ${advisees.length}`
              : data && data.advisorName
              ? `${data.advisorName}${viewingOther ? (isDean && !isSuperAdmin ? ' · viewing as dean' : ' · viewing as superadmin') : ''}`
              : 'Your advisees'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, color: KA.muted }}>
            <div style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>From</div>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
          </label>
          <label style={{ fontSize: 11, color: KA.muted }}>
            <div style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>To</div>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
          </label>
        </div>
      </div>

      {/* ---------- elevated (dean/superadmin): advisor picker + all-students ---------- */}
      {elevated && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: KA.wash,
            borderLeft: `3px solid ${KA.gold}`,
            fontSize: 13,
          }}
        >
          {viewingOther ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: KA.muted }}>
                Viewing{' '}
                <strong style={{ color: KA.navy }}>
                  {data.allStudents ? `all ${divisionLabel} students` : data.advisorName || viewAsStaffId}
                </strong>
                {isDean && !isSuperAdmin ? ' as dean.' : ' as superadmin.'}
              </span>
              {data.allStudents && (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <Btn
                    size="sm"
                    variant={loadedDivision !== 'middle' ? 'primary' : 'ghost'}
                    onClick={() => showAllStudents('upper')}
                  >
                    Upper School
                  </Btn>
                  <Btn
                    size="sm"
                    variant={loadedDivision === 'middle' ? 'primary' : 'ghost'}
                    onClick={() => showAllStudents('middle')}
                  >
                    Middle School
                  </Btn>
                </span>
              )}
              <Btn size="sm" variant="ghost" onClick={backToMe}>
                Back to my advisees
              </Btn>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: KA.muted }}>
                {isDean && !isSuperAdmin ? 'Dean' : 'Superadmin'} — view any advisor:
              </span>
              <div style={{ position: 'relative', width: 280 }}>
                <input
                  placeholder={advisorListLoading ? 'Loading advisors…' : 'Search by name'}
                  disabled={advisorListLoading}
                  value={advisorQuery}
                  onChange={(e) => setAdvisorQuery(e.target.value)}
                  style={{ ...inputStyle, width: '100%' }}
                />
                {advisorQuery.trim() && advisorMatches.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      background: KA.white,
                      border: `1px solid ${KA.line}`,
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      maxHeight: 260,
                      overflowY: 'auto',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                    }}
                  >
                    {advisorMatches.map((a) => (
                      <div
                        key={a.staffId}
                        role="button"
                        tabIndex={0}
                        // onMouseDown fires before the input's blur, so the row
                        // can't be pulled out from under the click.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickAdvisor(a);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            pickAdvisor(a);
                          }
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f7fb')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        style={{
                          padding: '7px 9px',
                          cursor: 'pointer',
                          borderTop: `1px solid ${KA.line}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 10,
                          alignItems: 'baseline',
                          background: 'transparent',
                        }}
                      >
                        <span style={{ color: KA.navy }}>
                          {a.name}
                          {!a.resolved && (
                            <span style={{ color: KA.red, fontSize: 11 }}> · no staff match</span>
                          )}
                        </span>
                        <span style={{ color: KA.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {a.adviseeCount} {a.adviseeCount === 1 ? 'advisee' : 'advisees'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {advisorQuery.trim() && !advisorListLoading && advisorMatches.length === 0 && (
                  <div style={{ padding: '6px 2px', fontSize: 12, color: KA.muted }}>
                    No advisor matches that name.
                  </div>
                )}
              </div>
              {advisorList.length > 0 && (
                <span style={{ color: KA.muted, fontSize: 12 }}>{advisorList.length} advisors</span>
              )}
              <span style={{ color: KA.line }}>|</span>
              <Btn size="sm" variant="outline" onClick={() => showAllStudents('upper')}>
                All Upper School
              </Btn>
              <Btn size="sm" variant="outline" onClick={() => showAllStudents('middle')}>
                All Middle School
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* ---------- error ---------- */}
      {error && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: '#fdf0ef',
            borderLeft: `3px solid ${KA.red}`,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ---------- body ---------- */}
      {loading ? (
        <div style={{ padding: 40, color: KA.muted }}>Loading…</div>
      ) : !data ? null : !data.isStaff ? (
        <EmptyState
          title="You're not on the staff roster"
          body="This account isn't matched to a staff record, so there are no advisees to show. If that's wrong, it's usually an email mismatch between your login and your /staff record."
        />
      ) : !data.isAdvisor ? (
        <EmptyState
          title="No advisees found"
          body={
            data.viewingOther
              ? 'This staff member has no students pointing at them as advisor.'
              : "You're on the staff roster, but no students list you as their advisor for this year."
          }
        />
      ) : (
        <>
          {/* ---------- stats ---------- */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 4,
              marginTop: 8,
              borderBottom: `1px solid ${KA.line}`,
            }}
          >
            <Stat label="Advisees" value={advisees.length} />
            {buckets.map((b) => (
              <Stat key={b.key} label={b.label} value={totals[b.key] || 0} tone={b.color} />
            ))}
            <div style={{ marginLeft: 'auto', fontSize: 11, color: KA.muted, textAlign: 'right' }}>
              {data.range ? `${data.range.datesScanned} school days` : ''}
              <br />
              {prettyDate(startDate)} – {prettyDate(endDate)}
            </div>
          </div>

          {/* ---------- uncategorized status warning ---------- */}
          {data.uncategorizedTotal > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                background: '#fdf0ef',
                borderLeft: `3px solid ${KA.red}`,
                fontSize: 13,
              }}
            >
              {data.uncategorizedTotal} attendance record
              {data.uncategorizedTotal === 1 ? '' : 's'} had a status not mapped to any column, so
              they aren't counted. Unmapped statuses:{' '}
              <code>
                {Object.entries(data.unmappedStatuses || {})
                  .map(([s, n]) => `${s} (${n})`)
                  .join(', ') || '—'}
              </code>
              . Add these to a bucket's <code>statuses</code> in{' '}
              <code>/config/attendanceCategories</code> to include them.
            </div>
          )}

          {/* ---------- controls ---------- */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '18px 0 10px' }}>
            <input
              placeholder="Search name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, width: 200 }}
            />

            {/* Filters — shown for elevated (dean/superadmin) views where the
                list is large enough to warrant slicing. */}
            {elevated && (
              <>
                {gradeOptions.length > 1 && (
                  <select
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto' }}
                    title="Filter by grade"
                  >
                    <option value="">All grades</option>
                    {gradeOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                )}
                {cohortOptions.length > 1 && (
                  <select
                    value={cohortFilter}
                    onChange={(e) => setCohortFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto' }}
                    title="Filter by cohort"
                  >
                    <option value="">All cohorts</option>
                    {cohortOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
                {advisorOptions.length > 1 && (
                  <select
                    value={advisorFilter}
                    onChange={(e) => setAdvisorFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', maxWidth: 200 }}
                    title="Filter by advisor"
                  >
                    <option value="">All advisors</option>
                    {advisorOptions.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                )}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: needsAttention ? KA.red : KA.muted,
                    cursor: 'pointer',
                    fontWeight: needsAttention ? 700 : 400,
                  }}
                  title="Students with an unexcused absence or a course below 70"
                >
                  <input
                    type="checkbox"
                    checked={needsAttention}
                    onChange={(e) => setNeedsAttention(e.target.checked)}
                  />
                  Needs attention
                </label>
                {(gradeFilter || cohortFilter || advisorFilter || needsAttention) && (
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setGradeFilter('');
                      setCohortFilter('');
                      setAdvisorFilter('');
                      setNeedsAttention(false);
                    }}
                  >
                    Clear filters
                  </Btn>
                )}
              </>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: KA.muted }}>
                {rows.length} of {advisees.length}
              </span>
              {elevated && (
                <Btn variant="ghost" size="sm" onClick={() => setShowCoAdvisors(true)} title="Assign co-advisors to advisories">
                  Co-advisors
                </Btn>
              )}
              <Btn variant="ghost" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
                Export CSV
              </Btn>
              <Btn
                variant="outline"
                size="sm"
                onClick={() => { setReportStudent(null); setReportMode('batch'); }}
                disabled={advisees.length === 0}
              >
                Download reports (PDF)
              </Btn>
            </div>
          </div>

          {/* ---------- table ----------
              NOTE: no `overflow` wrappers here. A wrapping overflow (hidden/auto)
              silently disables `position: sticky` on the header cells, so the
              card and the old horizontal-scroll div are intentionally plain
              divs. The <Th sticky> cells freeze the header row to the viewport
              (just under Layout's top bar) as the roster scrolls. */}
          <div style={{ border: `1px solid ${KA.line}`, borderRadius: 6 }}>
            <div>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12.5,
                  minWidth: tableMinWidth,
                  tableLayout: 'fixed',
                }}
              >
                <thead>
                  <tr style={{ background: KA.wash, textAlign: 'left' }}>
                    <Th onClick={() => toggleSort('name')} active={sortKey === 'name'} dir={sortDir} width={170} sticky>
                      Student
                    </Th>
                    <Th width={58} sticky>ID</Th>
                    <Th width={46} sticky>Grade</Th>
                    <Th width={64} sticky>Cohort</Th>
                    {buckets.map((b) => (
                      <Th
                        key={b.key}
                        onClick={() => toggleSort(b.key)}
                        active={sortKey === b.key}
                        dir={sortDir}
                        align="center"
                        width={72}
                        wrap
                        sticky
                      >
                        {b.label}
                      </Th>
                    ))}
                    <Th
                      onClick={() => toggleSort('redCount')}
                      active={sortKey === 'redCount'}
                      dir={sortDir}
                      align="center"
                      width={92}
                      wrap
                      sticky
                    >
                      Grades
                    </Th>
                    <Th onClick={() => toggleSort('lastDate')} active={sortKey === 'lastDate'} dir={sortDir} width={72} sticky>
                      Recent
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6 + buckets.length} style={{ padding: 28, textAlign: 'center', color: KA.muted }}>
                        No advisees match this search.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => {
                    const isSel = String(r.personId) === String(selected);
                    return (
                      <React.Fragment key={r.personId}>
                        <tr
                          onClick={() => setSelected(isSel ? null : r.personId)}
                          style={{
                            borderTop: `1px solid ${KA.line}`,
                            cursor: 'pointer',
                            background: isSel ? '#f4f7fb' : KA.white,
                          }}
                        >
                          <Td style={{ fontWeight: 600, color: KA.navy }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, maxWidth: 160 }}>
                              <span
                                title={r.name || ''}
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {r.name || '—'}
                              </span>
                              {r.coAdvised && (
                                <span
                                  title="You co-advise this advisory"
                                  style={{
                                    flex: '0 0 auto',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    color: KA.gold,
                                    border: `1px solid ${KA.gold}`,
                                    borderRadius: 3,
                                    padding: '0 3px',
                                  }}
                                >
                                  CO
                                </span>
                              )}
                            </span>
                          </Td>
                          <Td style={{ color: KA.muted, fontVariantNumeric: 'tabular-nums' }}>{r.personId}</Td>
                          <Td>{r.grade || '—'}</Td>
                          <Td style={{ color: KA.muted }}>{r.cohort || '—'}</Td>
                          {buckets.map((b) => (
                            <Td
                              key={b.key}
                              align="center"
                              style={{
                                fontWeight: r[b.key] ? 700 : 400,
                                color: r[b.key] ? b.color : KA.muted,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {r[b.key] || 0}
                            </Td>
                          ))}
                          <Td align="center">
                            <GradeDots courses={r.courses} />
                          </Td>
                          <Td style={{ color: KA.muted, whiteSpace: 'nowrap' }}>
                            {r.lastDate ? shortDate(r.lastDate) : '—'}
                          </Td>
                        </tr>

                        {/* Inline drill-down: full-width row directly under the
                            clicked student, so it opens where they clicked
                            rather than at the bottom of a long list. */}
                        {isSel && (
                          <tr>
                            <td
                              colSpan={6 + buckets.length}
                              style={{ padding: 0, background: '#f4f7fb', borderTop: `1px solid ${KA.line}` }}
                            >
                              <StudentDetail
                                student={r}
                                buckets={buckets}
                                fns={fns}
                                elevated={elevated}
                                scope={{
                                  allStudents: !!(data && data.allStudents),
                                  division: loadedDivision,
                                  viewAsStaffId:
                                    data && data.viewingOther && !data.allStudents ? viewAsStaffId : '',
                                }}
                                onClose={() => setSelected(null)}
                                onGenerate={() => { setReportStudent(r); setReportMode('single'); }}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---------- PDF report builder ---------- */}
          {reportMode && (
            <ReportBuilderModal
              mode={reportMode}
              student={reportStudent}
              students={advisees}
              advisorName={data?.advisorName}
              range={data?.range}
              buckets={buckets}
              onClose={() => setReportMode(null)}
            />
          )}

          {/* ---------- Co-advisor manager (elevated) ---------- */}
          {showCoAdvisors && (
            <CoAdvisorManager fns={fns} advisors={advisorList} onClose={() => setShowCoAdvisors(false)} />
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Student drill-down                                                  */
/* ------------------------------------------------------------------ */

function StudentDetail({ student, buckets, fns, scope, elevated, onClose, onGenerate }) {
  const events = Array.isArray(student.events) ? student.events : [];
  const bucketMap = useMemo(() => {
    const m = {};
    buckets.forEach((b) => (m[b.key] = b));
    return m;
  }, [buckets]);

  // Which block is this student most often flagged in?
  const byBlock = {};
  events.forEach((e) => {
    const k = e.block || '—';
    byBlock[k] = (byBlock[k] || 0) + 1;
  });
  const worstBlock = Object.entries(byBlock).sort((a, b) => b[1] - a[1])[0];

  // Unbucketed events (e.g. "Suspended") carry no bucketKey — show their raw
  // status instead of a generic "Unmapped status".
  const labelFor = (ev) => {
    const key = ev && ev.bucketKey;
    if (key && bucketMap[key]) return bucketMap[key].label;
    return (ev && ev.status) || 'Unmapped status';
  };
  const toneFor = (key) => (key && bucketMap[key] ? bucketMap[key].color : KA.muted);

  return (
    <div
      style={{
        borderTop: `3px solid ${KA.gold}`,
        background: KA.white,
        margin: '0 12px 12px',
        border: `1px solid ${KA.line}`,
        borderTopWidth: 3,
        borderTopColor: KA.gold,
        borderRadius: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 18px',
          borderBottom: `1px solid ${KA.line}`,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontFamily: DISPLAY, fontSize: 20, color: KA.navy }}>
            {student.name || `Student ${student.personId}`}
          </div>
          <div style={{ fontSize: 12, color: KA.muted, marginTop: 2 }}>
            ID {student.personId}
            {student.grade ? ` · ${student.grade}` : ''}
            {student.cohort ? ` · ${student.cohort}` : ''}
            {worstBlock && worstBlock[0] !== '—'
              ? ` · most often flagged in Block ${worstBlock[0]} (${worstBlock[1]}×)`
              : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onGenerate && (
            <Btn variant="outline" size="sm" onClick={onGenerate}>
              Report (PDF)
            </Btn>
          )}
          <Btn variant="ghost" size="sm" onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>

      {/* ---- Report card (grades) ---- */}
      <ReportCard courses={student.courses} />

      {/* ---- Grade history (snapshot trend) ---- */}
      <GradeHistory personId={student.personId} fns={fns} scope={scope} elevated={elevated} />

      {/* ---- Attendance events ---- */}
      <div
        style={{
          padding: '12px 18px 4px',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: KA.muted,
          fontWeight: 600,
          borderTop: `1px solid ${KA.line}`,
        }}
      >
        Attendance
      </div>
      {events.length === 0 ? (
        <div style={{ padding: '4px 18px 20px', color: KA.muted, fontSize: 13 }}>
          No absences or tardies in this range. A clean record.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 640 }}>
            <thead>
              <tr style={{ background: KA.wash, textAlign: 'left' }}>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th>Block</Th>
                <Th>Class</Th>
                <Th>Teacher</Th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={`${e.date}_${e.block}_${i}`} style={{ borderTop: `1px solid ${KA.line}` }}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{prettyDate(e.date)}</Td>
                  <Td>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontWeight: 600,
                        background: toneFor(e.bucketKey) + '1a',
                        color: toneFor(e.bucketKey),
                      }}
                      title={e.status || ''}
                    >
                      {labelFor(e)}
                    </span>
                  </Td>
                  <Td>{e.block || '—'}</Td>
                  <Td style={{ color: KA.muted }}>{e.class || '—'}</Td>
                  <Td style={{ color: KA.muted }}>{e.teacherName || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Grade dots — compact at-a-glance strip for the table               */
/* ------------------------------------------------------------------ */

function GradeDots({ courses }) {
  const { graded } = splitCourses(courses);
  if (graded.length === 0) {
    return <span style={{ color: KA.muted, fontSize: 12 }}>—</span>;
  }
  // One dot per graded course, colored by band, lowest scores first so a
  // red dot leads. Title shows the score on hover.
  return (
    <span style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 88 }}>
      {graded.map((c, i) => (
        <span
          key={`${c.courseCode || c.courseName || i}`}
          title={`${c.courseName || 'Course'}: ${roundScore(c.currentScore)}`}
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: bandColor(roundScore(c.currentScore)),
            display: 'inline-block',
          }}
        />
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Report card — full color-banded course list in the drill-down       */
/* ------------------------------------------------------------------ */

function ReportCard({ courses }) {
  const { graded, ungraded } = splitCourses(courses);
  const [showUngraded, setShowUngraded] = useState(false);

  return (
    <div style={{ padding: '14px 18px 6px' }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: KA.muted,
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        Report card
      </div>

      {graded.length === 0 ? (
        <div style={{ color: KA.muted, fontSize: 13, paddingBottom: 6 }}>
          No graded courses on file{ungraded.length ? '' : ' yet'}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {graded.map((c, i) => {
            const color = bandColor(c.currentScore);
            return (
              <div
                key={`${c.courseCode || c.courseName || i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: `1px solid ${KA.line}`,
                  borderLeft: `4px solid ${color}`,
                  borderRadius: 6,
                  padding: '6px 10px',
                  minWidth: 180,
                }}
              >
                <span style={{ fontSize: 13, color: KA.navy, flex: 1 }}>
                  {c.courseName || c.courseCode || 'Course'}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {roundScore(c.currentScore)}
                </span>
                {c.canvasUrl && (
                  <a
                    href={c.canvasUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Open this student's Canvas page for this course"
                    style={{ fontSize: 11, color: KA.gold, textDecoration: 'none', whiteSpace: 'nowrap' }}
                  >
                    Canvas ↗
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {ungraded.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            onClick={() => setShowUngraded((v) => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              color: KA.muted,
              fontSize: 12,
              cursor: 'pointer',
              padding: 0,
              fontFamily: BODY,
            }}
          >
            {showUngraded ? '▾' : '▸'} {ungraded.length} course{ungraded.length === 1 ? '' : 's'} without grades
          </button>
          {showUngraded && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {ungraded.map((c, i) => (
                <span
                  key={`u_${c.courseCode || c.courseName || i}`}
                  style={{
                    fontSize: 12,
                    color: KA.muted,
                    border: `1px solid ${KA.line}`,
                    borderRadius: 6,
                    padding: '4px 8px',
                    background: KA.wash,
                  }}
                >
                  {c.courseName || c.courseCode || 'Course'}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportBuilderModal({ mode, student, students, advisorName, range, buckets, onClose }) {
  // mode: 'single' (one student) or 'batch' (all advisees)
  const [sections, setSections] = React.useState({
    reportCard: true,
    ungraded: false,
    attendanceSummary: true,
    attendanceDetail: false,
  });
  const [generating, setGenerating] = React.useState(false);

  const toggle = (key) => setSections((s) => ({ ...s, [key]: !s[key] }));

  const SECTION_OPTS = [
    { key: 'reportCard', label: 'Report card (grades)', hint: 'Color-banded course scores, dated.' },
    { key: 'ungraded', label: 'Courses without grades', hint: 'Lists ungraded / non-academic courses.' },
    { key: 'attendanceSummary', label: 'Attendance summary', hint: 'Counts of absences and tardies.' },
    { key: 'attendanceDetail', label: 'Attendance detail', hint: 'Every individual absence/tardy record.' },
  ];

  const targets = mode === 'batch' ? (students || []) : student ? [student] : [];

  const handleGenerate = async () => {
    if (targets.length === 0) return;
    setGenerating(true);
    try {
      // Sort batch alphabetically for a tidy packet.
      const ordered =
        mode === 'batch'
          ? [...targets].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
          : targets;
      const doc = buildAdvisoryReport(ordered, { sections, advisorName, range, buckets });
      const stamp = new Date().toISOString().slice(0, 10);
      const filename =
        mode === 'batch'
          ? `advisory-reports-${stamp}.pdf`
          : `report-${(student.name || student.personId || 'student').replace(/[^A-Za-z0-9]+/g, '-')}-${stamp}.pdf`;
      doc.save(filename);
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Could not generate the PDF: ${err.message || err}`);
    } finally {
      setGenerating(false);
    }
  };

  const anySection = Object.values(sections).some(Boolean);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,26,26,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 6, borderTop: `3px solid #E83530`,
          padding: 24, width: '100%', maxWidth: 460, maxHeight: '90vh',
          overflowY: 'auto', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        }}
      >
        <h3 style={{ fontFamily: 'Georgia, serif', color: '#233C5F', margin: '0 0 4px', fontWeight: 400 }}>
          {mode === 'batch' ? 'Generate reports for all advisees' : 'Generate report'}
        </h3>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
          {mode === 'batch'
            ? `One page per advisee — ${targets.length} student${targets.length === 1 ? '' : 's'}. `
            : `${student?.name || 'Student'}. `}
          Header, student details, and the "grades as of" date are always included.
        </div>

        <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
          {SECTION_OPTS.map((opt) => (
            <label
              key={opt.key}
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
                border: `1px solid ${sections[opt.key] ? '#233C5F' : '#e5e7eb'}`,
                borderRadius: 6, padding: '10px 12px',
                background: sections[opt.key] ? '#f4f7fb' : '#fff',
              }}
            >
              <input
                type="checkbox"
                checked={sections[opt.key]}
                onChange={() => toggle(opt.key)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#233C5F' }}>{opt.label}</span>
                <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginTop: 1 }}>{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={generating}
            style={{
              padding: '8px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || !anySection || targets.length === 0}
            style={{
              padding: '8px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600,
              cursor: generating || !anySection ? 'not-allowed' : 'pointer',
              opacity: generating || !anySection ? 0.5 : 1,
              background: '#E83530', color: '#fff', border: 'none',
            }}
          >
            {generating ? 'Generating…' : mode === 'batch' ? `Download ${targets.length} reports` : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Grade history — per-course score trend across daily snapshots       */
/* ------------------------------------------------------------------ */

// Turn a getGradeHistory result into per-course rows with a plotted series,
// first/last/delta, and band colors. Lowest current score first so a sliding
// course leads; courses with no data at all are dropped.
function buildHistRows(hist) {
  const snaps = Array.isArray(hist.snapshots) ? hist.snapshots : [];
  const xMax = snaps.length - 1;

  const rows = (hist.courses || [])
    .map((c) => {
      const id = c.courseId;
      const points = [];
      snaps.forEach((s, i) => {
        const v = s && s.scores ? s.scores[id] : null;
        if (v != null && !Number.isNaN(Number(v))) points.push({ x: i, y: Number(v) });
      });

      const first = points.length ? Math.round(points[0].y) : null;
      const last = points.length
        ? Math.round(points[points.length - 1].y)
        : c.currentScore != null
        ? Math.round(c.currentScore)
        : null;
      const delta = first != null && last != null ? last - first : null;

      // Per-course y-domain so the shape of the change is visible, with a floor
      // on the span so a near-flat line isn't amplified into fake drama.
      let lo = Infinity;
      let hi = -Infinity;
      points.forEach((p) => {
        if (p.y < lo) lo = p.y;
        if (p.y > hi) hi = p.y;
      });
      if (!points.length) {
        lo = 0;
        hi = 100;
      }
      const MIN_SPAN = 6;
      if (hi - lo < MIN_SPAN) {
        const mid = (hi + lo) / 2;
        lo = mid - MIN_SPAN / 2;
        hi = mid + MIN_SPAN / 2;
      }
      lo -= 1;
      hi += 1;

      return {
        courseId: id,
        courseName: c.courseName || c.courseCode || `Course ${id}`,
        canvasUrl: c.canvasUrl || null,
        first,
        last,
        delta,
        firstColor: bandColor(first),
        lastColor: bandColor(last),
        series: { points, xMax, lo, hi },
        _sort: last == null ? Infinity : last,
      };
    })
    .filter((r) => r.series.points.length > 0 || r.last != null);

  rows.sort((a, b) => a._sort - b._sort);
  return rows;
}

// Tiny inline SVG line for one course. x is the snapshot index (so lines share
// a time axis); y is auto-scaled to the course's own range. Colored by the
// latest band. Dots only when the series is short enough to read.
function Sparkline({ series, color, width = 190, height = 34, pad = 5 }) {
  const pts = (series && series.points) || [];
  if (!pts.length) return <span style={{ color: KA.muted, fontSize: 11 }}>—</span>;

  const n = series.xMax; // index of last snapshot (>= 0)
  const lo = series.lo;
  const span = series.hi - series.lo || 1;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const X = (x) => pad + (n <= 0 ? innerW / 2 : (x / n) * innerW);
  const Y = (y) => pad + innerH - ((y - lo) / span) * innerH;

  const d = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${X(p.x).toFixed(1)},${Y(p.y).toFixed(1)}`)
    .join(' ');
  const last = pts[pts.length - 1];
  const showDots = pts.length <= 14;

  return (
    <svg width={width} height={height} style={{ display: 'block', flex: '0 0 auto' }} aria-hidden="true">
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {showDots &&
        pts.map((p, i) => <circle key={i} cx={X(p.x)} cy={Y(p.y)} r={1.8} fill={color} />)}
      <circle cx={X(last.x)} cy={Y(last.y)} r={2.8} fill={color} />
    </svg>
  );
}

// Lazily fetches one student's snapshot history via getGradeHistory (grades are
// server-only, so this callable is the only read path) and renders a trend row
// per course. Re-fetches when the student or the scope changes.
function GradeHistory({ personId, fns, scope, elevated }) {
  const [state, setState] = useState({ loading: true, error: null, hist: null });

  // Primitive scope fields so the effect doesn't refire on object identity.
  const sAll = !!(scope && scope.allStudents);
  const sView = (scope && scope.viewAsStaffId) || '';
  const sDiv = (scope && scope.division) || 'upper';

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, hist: null });
    (async () => {
      try {
        const fn = httpsCallable(fns, 'getGradeHistory');
        const payload = { personId };
        if (sAll) {
          payload.allStudents = true;
          payload.division = sDiv;
        } else if (sView) {
          payload.viewAsStaffId = sView;
        }
        const res = await fn(payload);
        if (alive) setState({ loading: false, error: null, hist: res.data || null });
      } catch (err) {
        if (alive)
          setState({
            loading: false,
            error: err.message || 'Could not load grade history.',
            hist: null,
          });
      }
    })();
    return () => {
      alive = false;
    };
  }, [personId, fns, sAll, sView, sDiv]);

  const { loading, error, hist } = state;
  const rows = hist ? buildHistRows(hist) : [];

  return (
    <div style={{ padding: '2px 18px 14px' }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: KA.muted,
          fontWeight: 600,
          margin: '10px 0 8px',
        }}
      >
        Grade history
      </div>

      {loading ? (
        <div style={{ color: KA.muted, fontSize: 13 }}>Loading history…</div>
      ) : error ? (
        <div style={{ color: KA.red, fontSize: 13 }}>{error}</div>
      ) : !hist || !hist.snapshotCount || rows.length === 0 ? (
        <div style={{ color: KA.muted, fontSize: 13 }}>
          No history on file yet. Trend lines appear once the daily grade snapshot has run on more
          than one day.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: KA.muted, marginBottom: 10 }}>
            {hist.snapshotCount} snapshot{hist.snapshotCount === 1 ? '' : 's'} ·{' '}
            {shortDate(hist.firstDate)} – {shortDate(hist.lastDate)}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {rows.map((row) => (
              <div
                key={row.courseId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                  border: `1px solid ${KA.line}`,
                  borderLeft: `4px solid ${row.lastColor}`,
                  borderRadius: 6,
                  padding: '6px 10px',
                }}
              >
                <span style={{ fontSize: 13, color: KA.navy, minWidth: 150, flex: 1 }}>
                  {row.courseName}
                  {row.canvasUrl && (
                    <a
                      href={row.canvasUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Open this student's Canvas gradebook for this course"
                      style={{
                        fontSize: 11,
                        color: KA.gold,
                        textDecoration: 'none',
                        marginLeft: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Canvas ↗
                    </a>
                  )}
                </span>
                <Sparkline series={row.series} color={row.lastColor} />
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 13,
                    minWidth: 96,
                    justifyContent: 'flex-end',
                  }}
                  title={`Start ${row.first == null ? '—' : row.first} → latest ${
                    row.last == null ? '—' : row.last
                  }`}
                >
                  <span style={{ color: row.firstColor }}>{row.first == null ? '—' : row.first}</span>
                  <span style={{ color: KA.muted }}>→</span>
                  <span style={{ color: row.lastColor, fontWeight: 700 }}>
                    {row.last == null ? '—' : row.last}
                  </span>
                  {row.delta != null && row.delta !== 0 && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: row.delta > 0 ? GRADE_BANDS.green : KA.red,
                      }}
                    >
                      {row.delta > 0 ? '▲' : '▼'}
                      {Math.abs(row.delta)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Co-advisor manager (dean/superadmin)                                */
/* ------------------------------------------------------------------ */

// Assign co-advisors to whole advisories. A co-advisor sees the primary
// advisor's entire advisory (roster, grades, attendance, reports, digest) and
// the assignment follows roster changes from the weekly SIS sync automatically,
// because it's resolved at read time — nothing is copied onto student records.
function CoAdvisorManager({ fns, advisors, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advisories, setAdvisories] = useState([]); // existing co-advisories
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);

  const [primary, setPrimary] = useState(null); // { staffId, name }
  const [primaryQuery, setPrimaryQuery] = useState('');
  const [coQuery, setCoQuery] = useState('');
  const [editCos, setEditCos] = useState([]); // [{ staffId, name }]

  const loadConfig = useCallback(async () => {
    const res = await httpsCallable(fns, 'getCoAdvisorConfig')({});
    return (res.data && res.data.advisories) || [];
  }, [fns]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [cfg, staffRes] = await Promise.all([
          loadConfig(),
          httpsCallable(fns, 'listStaff')({}),
        ]);
        if (!alive) return;
        setAdvisories(cfg);
        setStaff((staffRes.data && staffRes.data.staff) || []);
      } catch (e) {
        if (alive) setError(e.message || 'Could not load co-advisor settings.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [fns, loadConfig]);

  // Seed the editable list from config whenever a primary advisory is selected.
  useEffect(() => {
    if (!primary) {
      setEditCos([]);
      return;
    }
    const existing = advisories.find((a) => String(a.primaryAdvisorId) === String(primary.staffId));
    setEditCos(existing ? existing.coAdvisors.map((c) => ({ staffId: c.staffId, name: c.name })) : []);
  }, [primary, advisories]);

  const primaryMatches = useMemo(() => {
    const t = primaryQuery.trim().toLowerCase();
    if (!t) return [];
    return (advisors || [])
      .filter((a) => `${a.name} ${a.staffId}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [primaryQuery, advisors]);

  const coMatches = useMemo(() => {
    const t = coQuery.trim().toLowerCase();
    if (!t) return [];
    const taken = new Set(
      [primary && String(primary.staffId), ...editCos.map((c) => String(c.staffId))].filter(Boolean)
    );
    return staff
      .filter(
        (s) =>
          !taken.has(String(s.staffId)) &&
          `${s.name} ${s.email || ''} ${s.staffId}`.toLowerCase().includes(t)
      )
      .slice(0, 8);
  }, [coQuery, staff, editCos, primary]);

  async function persist(list) {
    if (!primary) return;
    setBusy(true);
    setError(null);
    try {
      await httpsCallable(fns, 'setCoAdvisorsForAdvisory')({
        primaryAdvisorId: primary.staffId,
        coAdvisorIds: list.map((c) => c.staffId),
      });
      setEditCos(list);
      setAdvisories(await loadConfig());
    } catch (e) {
      setError(e.message || 'Could not save that change.');
    } finally {
      setBusy(false);
    }
  }

  const addCo = (s) => {
    setCoQuery('');
    persist([...editCos, { staffId: s.staffId, name: s.name }]);
  };
  const removeCo = (staffId) => persist(editCos.filter((c) => String(c.staffId) !== String(staffId)));

  const chip = (label, onX, key) => (
    <span
      key={key}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: '#f4f7fb',
        border: `1px solid ${KA.line}`,
        borderRadius: 14,
        padding: '3px 6px 3px 10px',
        fontSize: 12.5,
        color: KA.navy,
      }}
    >
      {label}
      {onX && (
        <button
          onClick={onX}
          disabled={busy}
          title="Remove"
          style={{
            border: 'none',
            background: 'transparent',
            color: KA.muted,
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      )}
    </span>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26,26,26,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 6,
          borderTop: `3px solid ${KA.gold}`,
          padding: 24,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
          fontFamily: BODY,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <h3 style={{ fontFamily: DISPLAY, color: KA.navy, margin: '0 0 4px', fontWeight: 400, fontSize: 22 }}>
              Co-advisors
            </h3>
            <div style={{ fontSize: 12, color: KA.muted, maxWidth: 440 }}>
              A co-advisor sees the primary advisor&rsquo;s whole advisory — roster, grades,
              attendance, reports, and the weekly digest. Assignments follow roster changes
              automatically.
            </div>
          </div>
          <Btn variant="ghost" size="sm" onClick={onClose}>
            Close
          </Btn>
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fdf0ef', borderLeft: `3px solid ${KA.red}`, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 24, color: KA.muted }}>Loading…</div>
        ) : (
          <>
            {/* ---- pick an advisory ---- */}
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: KA.muted, fontWeight: 600, marginBottom: 6 }}>
                Advisory
              </div>
              {primary ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, color: KA.navy, fontWeight: 600 }}>{primary.name}</span>
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPrimary(null);
                      setPrimaryQuery('');
                      setCoQuery('');
                    }}
                  >
                    Choose another
                  </Btn>
                </div>
              ) : (
                <div style={{ position: 'relative', maxWidth: 360 }}>
                  <input
                    placeholder="Search a primary advisor by name"
                    value={primaryQuery}
                    onChange={(e) => setPrimaryQuery(e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                  {primaryQuery.trim() && primaryMatches.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        background: KA.white,
                        border: `1px solid ${KA.line}`,
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        maxHeight: 240,
                        overflowY: 'auto',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                      }}
                    >
                      {primaryMatches.map((a) => (
                        <div
                          key={a.staffId}
                          role="button"
                          tabIndex={0}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setPrimary({ staffId: a.staffId, name: a.name });
                            setPrimaryQuery('');
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f7fb')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          style={{
                            padding: '7px 9px',
                            cursor: 'pointer',
                            borderTop: `1px solid ${KA.line}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                          }}
                        >
                          <span style={{ color: KA.navy }}>{a.name}</span>
                          <span style={{ color: KA.muted, fontSize: 12 }}>
                            {a.ownCount != null ? a.ownCount : a.adviseeCount} advisees
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ---- edit co-advisors for the chosen advisory ---- */}
            {primary && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: KA.muted, fontWeight: 600, marginBottom: 6 }}>
                  Co-advisors {busy && <span style={{ textTransform: 'none', letterSpacing: 0 }}>· saving…</span>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {editCos.length === 0 ? (
                    <span style={{ color: KA.muted, fontSize: 13 }}>None yet.</span>
                  ) : (
                    editCos.map((c) => chip(c.name, () => removeCo(c.staffId), c.staffId))
                  )}
                </div>
                <div style={{ position: 'relative', maxWidth: 360 }}>
                  <input
                    placeholder="Add a co-advisor by name"
                    value={coQuery}
                    onChange={(e) => setCoQuery(e.target.value)}
                    disabled={busy}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                  {coQuery.trim() && coMatches.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        background: KA.white,
                        border: `1px solid ${KA.line}`,
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        maxHeight: 240,
                        overflowY: 'auto',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                      }}
                    >
                      {coMatches.map((s) => (
                        <div
                          key={s.staffId}
                          role="button"
                          tabIndex={0}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addCo(s);
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f7fb')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          style={{
                            padding: '7px 9px',
                            cursor: 'pointer',
                            borderTop: `1px solid ${KA.line}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                          }}
                        >
                          <span style={{ color: KA.navy }}>{s.name}</span>
                          <span style={{ color: KA.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {s.email || s.staffId}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ---- existing co-advisories ---- */}
            <div style={{ marginTop: 22, borderTop: `1px solid ${KA.line}`, paddingTop: 14 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: KA.muted, fontWeight: 600, marginBottom: 8 }}>
                Current co-advisories
              </div>
              {advisories.length === 0 ? (
                <div style={{ color: KA.muted, fontSize: 13 }}>No advisories have co-advisors yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {advisories.map((a) => (
                    <div
                      key={a.primaryAdvisorId}
                      style={{
                        border: `1px solid ${KA.line}`,
                        borderRadius: 6,
                        padding: '8px 12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontSize: 13.5, color: KA.navy, fontWeight: 600, minWidth: 150 }}>
                        {a.primaryName}
                        <span style={{ color: KA.muted, fontWeight: 400 }}> · {a.adviseeCount}</span>
                      </span>
                      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                        {a.coAdvisors.map((c) => chip(c.name, null, c.staffId))}
                      </span>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => setPrimary({ staffId: a.primaryAdvisorId, name: a.primaryName })}
                      >
                        Edit
                      </Btn>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

function EmptyState({ title, body }) {
  return (
    <div
      style={{
        marginTop: 24,
        padding: 40,
        textAlign: 'center',
        border: `1px dashed ${KA.line}`,
        borderRadius: 6,
        background: KA.wash,
      }}
    >
      <div style={{ fontFamily: DISPLAY, fontSize: 19, color: KA.navy }}>{title}</div>
      <p style={{ color: KA.muted, fontSize: 13, maxWidth: 460, margin: '10px auto 0' }}>{body}</p>
    </div>
  );
}